import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Loader2, ExternalLink, CheckCircle2, AlertCircle, Clock, Eye } from "lucide-react";
import { api } from "@/lib/api";
import ExamPreviewModal from "@/components/ExamPreviewModal";

export default function BatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewIdx, setPreviewIdx] = useState(null);
  const pollRef = useRef(null);

  const doneExamIds = batch
    ? batch.items.filter((it) => it.status === "done" && it.exam_id).map((it) => it.exam_id)
    : [];

  const load = async () => {
    try {
      const { data } = await api.get(`/batches/${id}`);
      setBatch(data);
      return data;
    } catch (e) {
      toast.error("Batch tidak ditemukan");
      navigate("/batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (!batch || ["done", "failed"].includes(batch.status)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => load(), 2000);
    return () => clearInterval(pollRef.current);
  }, [batch]);

  const remove = async () => {
    if (!window.confirm("Hapus batch ini beserta semua exam yang dibuat?")) return;
    try {
      await api.delete(`/batches/${id}?delete_exams=true`);
      toast.success("Batch dihapus");
      navigate("/batches");
    } catch (e) {
      toast.error("Gagal hapus");
    }
  };

  if (loading || !batch) {
    return (
      <div className="p-10 flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
      </div>
    );
  }

  const progress = ((batch.done + batch.failed) / batch.total) * 100;

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <Link
          to="/batches"
          data-testid="back-to-batches"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-3"
        >
          <ArrowLeft className="w-3 h-3" /> Riwayat Batch
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
              {batch.notes || `Batch ${batch.id.slice(0, 8)}`}
            </h1>
            <div className="text-sm text-zinc-500 mt-2 font-mono">
              {batch.total} file · {batch.exam_date || "-"} · {new Date(batch.created_at).toLocaleString()}
            </div>
          </div>
          <button
            onClick={remove}
            data-testid="delete-batch-detail-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 px-4 py-2 text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Hapus Batch
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 mb-6">
        <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="text-emerald-400 font-medium">{batch.done}</span>
            <span className="text-zinc-500"> done · </span>
            <span className="text-rose-400 font-medium">{batch.failed}</span>
            <span className="text-zinc-500"> failed · </span>
            <span className="text-zinc-400">{batch.total} total</span>
          </div>
          {!["done", "failed"].includes(batch.status) && (
            <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Memproses
            </span>
          )}
        </div>
        <div className="h-2 bg-zinc-800">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40">
        <div className="divide-y divide-zinc-900 max-h-[600px] overflow-y-auto">
          {batch.items.map((it) => (
            <ItemRow
              key={it.item_id}
              item={it}
              onPreview={
                it.exam_id
                  ? () => {
                      const idx = doneExamIds.indexOf(it.exam_id);
                      if (idx >= 0) setPreviewIdx(idx);
                    }
                  : null
              }
            />
          ))}
        </div>
      </div>

      {previewIdx !== null && doneExamIds.length > 0 && (
        <ExamPreviewModal
          examIds={doneExamIds}
          currentIndex={previewIdx}
          onClose={() => setPreviewIdx(null)}
          onIndexChange={setPreviewIdx}
        />
      )}
    </div>
  );
}

function ItemRow({ item, onPreview }) {
  const statusMap = {
    pending: { icon: <Clock className="w-4 h-4 text-zinc-500" />, text: "Menunggu" },
    processing: { icon: <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />, text: "Memproses..." },
    done: { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, text: `${item.findings ?? 0} temuan` },
    failed: { icon: <AlertCircle className="w-4 h-4 text-rose-400" />, text: item.error || "Gagal" },
  };
  const s = statusMap[item.status] || statusMap.pending;

  return (
    <div className="px-5 py-3 flex items-center gap-3 hover:bg-zinc-900/60 transition-colors">
      <div className="shrink-0">{s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate">{item.filename}</div>
        <div className="text-[10px] font-mono text-zinc-500 truncate">
          {item.patient_id} · {s.text}
        </div>
      </div>
      {item.status === "done" && item.exam_id && (
        <div className="flex items-center gap-1 shrink-0">
          {onPreview && (
            <button
              onClick={onPreview}
              className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded hover:bg-indigo-500/10"
              data-testid={`preview-item-${item.item_id}`}
              title="Preview cepat"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
          )}
          <Link
            to={`/exam/${item.exam_id}`}
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-100 px-2 py-1 rounded hover:bg-zinc-800"
          >
            Buka <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
